import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { SelectOption } from "@idw-types/common";
import { TimeRangeSearchFormFilterRow } from "../../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { TimeRangeTypes } from "@enums/time-range-types";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { SearchFormFilterRow } from "../../../common/search-form-filter-row/search-form-filter-row";
import { MultiSelectFormGroup } from "../../../common/multi-select-form-group/multi-select-form-group";
import { PolicyDecision, ServiceOffering } from "@idw-types/data/reference-data/generic";
import { sortStrings } from "@utils/string-util";
import { SelectFormGroup } from "../../../common/select-form-group/select-form-group";
import { ServiceOfferingMap } from "@idw-types/data/service-offering-map";
import { ServiceAssertionPerformanceService } from "@idw-types/data/reports";
import { ServiceAssertionPerformanceComparisonSearchFiltersState } from "@idw-types/state/service-assertion-performance-comparison-state";
import {
    mapSAPServiceRevisionsToSelectOptions,
    mapSAPServiceRevisionToSelectOption,
    mapRpServiceToSelectOption,
    mapRpServicesToSelectOptions,
    mapEntitiesWithValueAndKeyToSelectOptions,
    mapEntitiesWithNameAndIdToSelectOptions,
    mapEntityWithNameAndIdToSelectOption
} from "@utils/select-option-mapping-util";
import { initialServiceAssertionPerformanceComparisonState } from "@redux/reducers/service-assertion-performance-comparison-reducer";
import Button from "react-bootstrap/Button";
import styles from "./service-assertion-performance-comparison-search-form.less";
import { SearchFormHeaderContainer } from "../../../common/search-form-header-container/search-form-header-container";

type ServiceAssertionPerformanceComparisonSearchFormProps = {
    requestInProgress: boolean;
    policyDecisions: Array<PolicyDecision>;
    services: Array<ServiceAssertionPerformanceService>;
    serviceOfferings: Array<ServiceOffering>;
    serviceOfferingMap: Array<ServiceOfferingMap>;
    searchFilters: ServiceAssertionPerformanceComparisonSearchFiltersState;
    handleSearch: (searchFilters: ServiceAssertionPerformanceComparisonSearchFiltersState) => void;
};

type ServiceAssertionPerformanceComparisonSearchFormState = {
    selectedRpServiceOption: SelectOption;
    selectedRpServiceVersionOption: SelectOption;
    selectedPolicyDecisionOptions: Array<SelectOption>;
    selectedServiceOfferingOptions: Array<SelectOption>;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const ServiceAssertionPerformanceComparisonSearchForm = (props: ServiceAssertionPerformanceComparisonSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        policyDecisions,
        services,
        serviceOfferings,
        serviceOfferingMap,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (
        searchFilters: ServiceAssertionPerformanceComparisonSearchFiltersState
    ): ServiceAssertionPerformanceComparisonSearchFormState => {
        const selectedRpService = services.find(service => searchFilters.rpServiceId === service.id);
        const selectedRpServiceVersion = selectedRpService ? (selectedRpService.revisions || []).find(revision => searchFilters.version = revision.revision) : null;
        const selectedPolicyDecisions = policyDecisions.filter(policyDecision => searchFilters.policyDecisions.includes(policyDecision.key));
        const selectedServiceOfferings = serviceOfferings.filter(serviceOffering => searchFilters.serviceOfferingIds.includes(serviceOffering.id));

        return {
            selectedRpServiceOption: selectedRpService ? mapRpServiceToSelectOption(selectedRpService) : null,
            selectedRpServiceVersionOption: selectedRpServiceVersion ? mapSAPServiceRevisionToSelectOption(selectedRpServiceVersion) : null,
            selectedPolicyDecisionOptions: mapEntitiesWithValueAndKeyToSelectOptions(selectedPolicyDecisions),
            selectedServiceOfferingOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedServiceOfferings),
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<ServiceAssertionPerformanceComparisonSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialServiceAssertionPerformanceComparisonState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const rpServiceId = filterState.selectedRpServiceOption?.value || null;
        const version = filterState.selectedRpServiceVersionOption?.value || null;
        const policyDecisions = filterState.selectedPolicyDecisionOptions.map(pd => pd.value);
        const serviceOfferingIds = filterState.selectedServiceOfferingOptions.map(so => so.value);
        handleSearch({
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            rpServiceId,
            version,
            policyDecisions,
            serviceOfferingIds
        });
    };

    const handleUpdateRpServiceOption = (newValue: SelectOption) => {
        const selectedServiceOfferingOptions: Array<SelectOption> = [];
        if (newValue) {
            const availableServiceOfferingIds = serviceOfferingMap.filter(som => som.rpServiceID === newValue.value).map(som => som.serviceOfferingID);
            serviceOfferings.forEach(so => {
                if (availableServiceOfferingIds.includes(so.id)) {
                    selectedServiceOfferingOptions.push(mapEntityWithNameAndIdToSelectOption(so));
                }
            });
        }

        setFilterState({
            ...filterState,
            selectedRpServiceOption: newValue,
            selectedRpServiceVersionOption: null,
            selectedServiceOfferingOptions: selectedServiceOfferingOptions
        });
    };

    const handleUpdateRpServiceVersionOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedRpServiceVersionOption: newValue,
        });
    };

    const handleUpdatePolicyDecisionOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedPolicyDecisionOptions: newValue
        });
    };

    const handleUpdateServiceOfferingOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedServiceOfferingOptions: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const timeRangeTypes = [
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.LAST_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        return mapRpServicesToSelectOptions(services).sort((a, b) => sortStrings(a.label, b.label));
    }, [services]);

    const policyDecisionOptions: Array<SelectOption> = useMemo(() => {
        return mapEntitiesWithValueAndKeyToSelectOptions(policyDecisions);
    }, [policyDecisions]);

    const serviceOfferingOptions: Array<SelectOption> = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(serviceOfferings).sort((a, b) => sortStrings(a.label, b.label));
    }, [serviceOfferings]);

    const rpServiceVersionOptions: Array<SelectOption> = useMemo(() => {
        const selectedServiceId = filterState.selectedRpServiceOption?.value || null;
        const selectedService = selectedServiceId ? services.find(s => s.id === selectedServiceId) : null;
        if (selectedService) {
            return mapSAPServiceRevisionsToSelectOptions(selectedService.revisions || []);
        } else {
            return [];
        }
    }, [services, filterState.selectedRpServiceOption]);

    return (
        <Form className={styles.serviceAssertionPerformanceComparisonSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"service-assertion-performance-comparison"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <SelectFormGroup
                            automationId={`service-assertion-performance-comparison-search-filters-service-select`}
                            controlId={`service-assertion-performance-comparison-search-filters-service-select`}
                            label={resources.common.searchForm.filters.verificationService.label}
                            ariaLabel={resources.common.searchForm.filters.verificationService.label}
                            onChange={handleUpdateRpServiceOption}
                            options={rpServiceOptions}
                            selectedOption={filterState.selectedRpServiceOption}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                        <MultiSelectFormGroup
                            automationId={`service-assertion-performance-comparison-search-filters-policy-decisions-select`}
                            controlId={`service-assertion-performance-comparison-search-filters-policy-decisions-select`}
                            label={resources.common.searchForm.filters.policyDecisions.label}
                            ariaLabel={resources.common.searchForm.filters.policyDecisions.label}
                            onChangeMulti={handleUpdatePolicyDecisionOptions}
                            options={policyDecisionOptions}
                            selectedOptions={filterState.selectedPolicyDecisionOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={false}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <SelectFormGroup
                            automationId={`service-assertion-performance-comparison-search-filters-version-select`}
                            controlId={`service-assertion-performance-comparison-search-filters-version-select`}
                            label={resources.pages.serviceAssertionPerformanceComparison.searchForm.filters.verificationServiceRevision.label}
                            ariaLabel={resources.pages.serviceAssertionPerformanceComparison.searchForm.filters.verificationServiceRevision.label}
                            onChange={handleUpdateRpServiceVersionOption}
                            options={rpServiceVersionOptions}
                            selectedOption={filterState.selectedRpServiceVersionOption}
                            disabled={filterState.selectedRpServiceOption === null}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                        />
                        <MultiSelectFormGroup
                            automationId={`service-assertion-performance-comparison-search-filters-service-offerings-select`}
                            controlId={`service-assertion-performance-comparison-search-filters-service-offerings-select`}
                            label={resources.common.searchForm.filters.serviceOfferings.label}
                            ariaLabel={resources.common.searchForm.filters.serviceOfferings.label}
                            onChangeMulti={handleUpdateServiceOfferingOptions}
                            options={serviceOfferingOptions}
                            selectedOptions={filterState.selectedServiceOfferingOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        idPrefix={"service-assertion-performance-comparison-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={false}
                        showYearDropdown={false}
                        showMonthYearPicker={true}
                    />
                    <div className={styles.buttonDiv}>
                        <Button id="service-assertion-performance-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="service-assertion-performance-search-submit-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};